/**
 * What the nav badges are allowed to say.
 *
 * A count used to cross the screen-to-shell boundary as a bare number, and a bare number
 * carries neither the scope it describes nor whether it is known yet. Both screens keep
 * the previous response on screen while a new request runs, so a count derived from it
 * answers the scope the reader has left, and a failed request leaves that wrong count up
 * indefinitely. The badge is therefore a function of the scope alone (GRO-177): the shell
 * reads the scope once and derives both numbers from that one response, so which screen is
 * mounted cannot move them and neither can a view filter inside the open screen.
 */

import { type ConfidenceThresholds, thresholdsOf } from '../creatives/confidence'
import { type CreativesResponse, scopePartial } from '../creatives/contract'
import { pool } from '../hypotheses/pool'

/** The shared reporting scope: the active client, the accounts in scope and the period. */
export interface ReportingScope {
  clientId: string
  /** `null` is the company's default scope; `[]` makes no request at all. */
  accountIds: string[] | null
  /** The window's own key, from `periodKey`: a 14-day preset and a 14-day range differ. */
  period: string
  /**
   * Raised whenever the client's set of accounts changes under the reader (GRO-41): with
   * every box checked the scope is `null` before and after a connect or a demo removal,
   * so without this term nothing in the key would change and the screens would keep
   * figures from accounts that are gone.
   */
  accountsRevision?: number
}

/**
 * The shared scope as one comparable value.
 *
 * Opaque to the screens: they receive it, hold it beside the response it asked for, and
 * hand it back with the count. Only the shell reads it, and only to compare.
 */
export function scopeKey({ clientId, accountIds, period, accountsRevision = 0 }: ReportingScope): string {
  return `${selectionKey({ clientId, accountIds, accountsRevision })}|${period}`
}

/**
 * The company and its account selection, without the window: what the range picker's
 * bounds belong to, because a selection's history does not change when the window does.
 */
export function selectionKey({ clientId, accountIds, accountsRevision = 0 }: Omit<ReportingScope, 'period'>): string {
  return `${clientId}|${accountIds === null ? '*' : accountIds.join(',')}|r${accountsRevision}`
}

/** Both nav counts, read off one unfiltered scope response, and the tiers it was judged with. */
export interface ScopeCounts {
  creatives: number
  rollups: number
  thresholds: ConfidenceThresholds
}

/**
 * Both nav badges from one response read for the scope, with no view state applied.
 *
 * `buildRows` is one row per creative, so `creatives` is the number the table holds with
 * no filters, and the hypothesis pool is the bucket set the Ideas screen shows on that
 * axis whatever axis the reader has selected. The empty company answers with no creatives
 * and no buckets, so both numbers are a real `0` rather than an invented one.
 */
export function scopeCounts(response: CreativesResponse): ScopeCounts {
  const thresholds = thresholdsOf(response.verdictBasis)
  return {
    creatives: response.creatives.length,
    rollups: pool(response.creatives, 'hypothesis', thresholds, scopePartial(response.scope)).length,
    thresholds,
  }
}
